import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Phone, Eye, EyeOff, ArrowLeft, Shield, KeyRound } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const tx = (lang, fr, en, pt) => lang === 'fr' ? fr : lang === 'pt' ? pt : en;
const MoonRiverLogoAuth = () => (
  <img
    src="/logo-full-transparent.png"
    alt="Moon River Real Estate"
    className="h-28 w-auto object-contain mx-auto"
  />
);

const LoginPage = () => {
  const { t, language } = useLanguage();
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [error, setError] = useState('');

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return tx(language, 'Une erreur est survenue', 'Something went wrong', 'Ocorreu um erro');
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await login(formData.email, formData.password);
      toast.success(tx(language, 'Accès autorisé', 'Access granted', 'Acesso autorizado'));
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="login-page">
      {/* Left Panel - Decorative */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1B]/80 via-[#1A1A1B]/60 to-[#1A1A1B]/90" />

        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E5B1A1] to-transparent" />
          <div className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E5B1A1] to-transparent" />
          <div className="absolute top-3/4 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E5B1A1] to-transparent" />
        </div>

        <div className="relative z-10 flex flex-col justify-center items-center p-12 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}>
            <div className="w-24 h-24 rounded-full border-2 border-[#E5B1A1]/30 flex items-center justify-center mb-8 mx-auto">
              <div className="w-16 h-16 rounded-full border border-[#E5B1A1]/50 flex items-center justify-center">
                <Shield size={32} className="text-[#E5B1A1]" strokeWidth={1} />
              </div>
            </div>
            <h2 className="font-serif text-4xl text-white mb-4">
              {tx(language, 'Cabinet Confidentiel', 'Private Cabinet', 'Gabinete Confidencial')}
            </h2>
            <p className="text-white/70 text-lg max-w-md leading-relaxed">
              {tx(language, "Accédez à votre espace privé et découvrez nos opportunités d'investissement exclusives.", "Access your private space and discover our exclusive investment opportunities.", "Accédez à votre espace privé et découvrez nos opportunités d'investissement exclusives.")}
            </p>
            <div className="mt-12 flex items-center justify-center gap-4">
              <div className="w-16 h-px bg-gradient-to-r from-transparent to-[#E5B1A1]/50" />
              <KeyRound size={20} className="text-[#E5B1A1]/50" strokeWidth={1} />
              <div className="w-16 h-px bg-gradient-to-l from-transparent to-[#E5B1A1]/50" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right Panel - Login Form on WHITE with Glassmorphism */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12 bg-white">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10 w-full max-w-md"
        >
          <Link
            to="/"
            className="flex items-center gap-2 text-[#6B6B6B] hover:text-[#E5B1A1] transition-colors mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft size={18} />
            <span>{t('common.back')}</span>
          </Link>

          {/* Glassmorphism Card */}
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-8 h-8 border-l border-t border-[#E5B1A1]/30" />
            <div className="absolute -top-4 -right-4 w-8 h-8 border-r border-t border-[#E5B1A1]/30" />
            <div className="absolute -bottom-4 -left-4 w-8 h-8 border-l border-b border-[#E5B1A1]/30" />
            <div className="absolute -bottom-4 -right-4 w-8 h-8 border-r border-b border-[#E5B1A1]/30" />

            <div className="bg-white/60 backdrop-blur-xl border border-[#E5E5E5] p-8 md:p-10 shadow-lg">
              <div className="text-center mb-10">
                <Link to="/" className="inline-block mb-8">
                  <MoonRiverLogoAuth />
                </Link>
                <div className="flex items-center justify-center gap-3 mb-4">
                  <div className="w-2 h-2 rounded-full bg-[#E5B1A1] animate-pulse" />
                  <span className="text-[#E5B1A1] text-xs uppercase tracking-[0.3em] font-medium">
                    {tx(language, 'Accès Sécurisé', 'Secure Access', 'Acesso Seguro')}
                  </span>
                  <div className="w-2 h-2 rounded-full bg-[#E5B1A1] animate-pulse" />
                </div>
                <h1 className="font-serif text-3xl text-[#1A1A1B] mb-2">
                  {tx(language, 'Espace Client', 'Client Area', 'Área do Cliente')}
                </h1>
                <p className="text-[#6B6B6B]">
                  {tx(language, 'Identifiez-vous pour accéder à votre espace privé', 'Sign in to access your private space', 'Identifique-se para aceder ao seu espaço privado')}
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 p-4 mb-6 text-sm" data-testid="login-error">
                  <div className="flex items-center gap-2">
                    <Shield size={16} className="text-red-500" />
                    {error}
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
                <div>
                  <label className="text-[#E5B1A1] text-xs uppercase tracking-[0.2em] font-medium block mb-3">
                    {t('auth.login.email')}
                  </label>
                  <div className="relative group">
                    <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A3A3A0] group-focus-within:text-[#E5B1A1] transition-colors" />
                    <Input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full pl-12 py-4 bg-[#F5F5F5] border-[#E5E5E5] text-[#1A1A1B] focus:border-[#E5B1A1] focus:ring-1 focus:ring-[#E5B1A1]/30 transition-all"
                      placeholder="email@example.com"
                      data-testid="login-email"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[#E5B1A1] text-xs uppercase tracking-[0.2em] font-medium block mb-3">
                    {t('auth.login.password')}
                  </label>
                  <div className="relative group">
                    <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A3A3A0] group-focus-within:text-[#E5B1A1] transition-colors" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full pl-12 pr-12 py-4 bg-[#F5F5F5] border-[#E5E5E5] text-[#1A1A1B] focus:border-[#E5B1A1] focus:ring-1 focus:ring-[#E5B1A1]/30 transition-all"
                      placeholder="••••••••"
                      data-testid="login-password"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A3A3A0] hover:text-[#E5B1A1] transition-colors">
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Button: Noir Mat bg, Or Rosé text */}
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full py-4 text-base font-medium tracking-wide transition-all duration-300 border-0"
                  style={{ backgroundColor: '#1A1A1B', color: '#E5B1A1' }}
                  data-testid="login-submit"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-[#E5B1A1]/30 border-t-[#E5B1A1] rounded-full animate-spin" />
                      <span>{tx(language, 'Vérification...', 'Verifying...', 'A verificar...')}</span>
                    </div>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <KeyRound size={18} />
                      {tx(language, 'Accéder', 'Enter', 'Aceder')}
                    </span>
                  )}
                </Button>
              </form>

              <div className="flex items-center gap-4 my-8">
                <div className="flex-1 h-px bg-[#E5E5E5]" />
                <span className="text-[#A3A3A0] text-xs uppercase tracking-wider">{t('auth.login.orContinueWith')}</span>
                <div className="flex-1 h-px bg-[#E5E5E5]" />
              </div>

              <Button
                type="button"
                onClick={loginWithGoogle}
                variant="outline"
                className="w-full py-4 border-[#E5E5E5] text-[#1A1A1B] hover:bg-[#F5F5F5] hover:border-[#E5B1A1]/50 transition-all"
                data-testid="google-login"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#E5B1A1" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#E5B1A1" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#D49B8B" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#D49B8B" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Google
              </Button>

              <p className="text-center text-[#6B6B6B] mt-8 text-sm">
                {t('auth.login.noAccount')}{' '}
                <Link to="/register" className="text-[#E5B1A1] hover:text-[#D49B8B] transition-colors font-medium" data-testid="register-link">
                  {t('auth.login.register')}
                </Link>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const RegisterPage = () => {
  const { t, language } = useLanguage();
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [error, setError] = useState('');

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return tx(language, 'Une erreur est survenue', 'Something went wrong', 'Ocorreu um erro');
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setError(tx(language, 'Les mots de passe ne correspondent pas', 'Passwords do not match', 'As palavras-passe não coincidem'));
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await register(formData.name, formData.email, formData.password, formData.phone);
      toast.success(tx(language, 'Bienvenue dans le Cabinet', 'Welcome to the Cabinet', 'Bem-vindo ao Gabinete'));
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const inputClasses = "w-full pl-12 py-3 bg-[#F5F5F5] border-[#E5E5E5] text-[#1A1A1B] focus:border-[#E5B1A1]";

  return (
    <div className="min-h-screen flex bg-white" data-testid="register-page">
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1571071956127-6b8d711f87c6?w=1920&q=80')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1B]/85 via-[#1A1A1B]/70 to-[#1A1A1B]/90" />
        <div className="absolute inset-0 opacity-15">
          <div className="absolute top-1/3 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E5B1A1] to-transparent" />
          <div className="absolute top-2/3 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E5B1A1] to-transparent" />
        </div>
        <div className="relative z-10 flex flex-col justify-center items-center p-12 text-center">
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}>
            <div className="w-20 h-20 rounded-full border border-[#E5B1A1]/30 flex items-center justify-center mb-8 mx-auto">
              <User size={28} className="text-[#E5B1A1]" strokeWidth={1} />
            </div>
            <h2 className="font-serif text-4xl text-white mb-4">
              {tx(language, 'Rejoignez le Cabinet', 'Join the Cabinet', 'Junte-se ao Gabinete')}
            </h2>
            <p className="text-white/70 text-lg max-w-md leading-relaxed">
              {tx(language, "Créez votre accès privilégié et accédez à un univers d'opportunités exclusives.", "Create your privileged access and discover a world of exclusive opportunities.", "Créez votre accès privilégié et accédez à un univers d'opportunités exclusives.")}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12 overflow-y-auto bg-white">
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="relative z-10 w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 text-[#6B6B6B] hover:text-[#E5B1A1] transition-colors mb-8">
            <ArrowLeft size={18} />
            <span>{t('common.back')}</span>
          </Link>

          <div className="relative">
            <div className="absolute -top-4 -left-4 w-8 h-8 border-l border-t border-[#E5B1A1]/30" />
            <div className="absolute -top-4 -right-4 w-8 h-8 border-r border-t border-[#E5B1A1]/30" />
            <div className="absolute -bottom-4 -left-4 w-8 h-8 border-l border-b border-[#E5B1A1]/30" />
            <div className="absolute -bottom-4 -right-4 w-8 h-8 border-r border-b border-[#E5B1A1]/30" />

            <div className="bg-white/60 backdrop-blur-xl border border-[#E5E5E5] p-8 md:p-10 shadow-lg">
              <div className="text-center mb-8">
                <Link to="/" className="inline-block mb-6"><MoonRiverLogoAuth /></Link>
                <h1 className="font-serif text-2xl text-[#1A1A1B] mb-2">{t('auth.register.title')}</h1>
                <p className="text-[#6B6B6B] text-sm">{t('auth.register.subtitle')}</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 p-4 mb-6 text-sm" data-testid="register-error">{error}</div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
                <div>
                  <label className="text-[#E5B1A1] text-xs uppercase tracking-[0.2em] font-medium block mb-2">{t('auth.register.name')}</label>
                  <div className="relative">
                    <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A3A3A0]" />
                    <Input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={inputClasses} data-testid="register-name" />
                  </div>
                </div>
                <div>
                  <label className="text-[#E5B1A1] text-xs uppercase tracking-[0.2em] font-medium block mb-2">{t('auth.register.email')}</label>
                  <div className="relative">
                    <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A3A3A0]" />
                    <Input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={inputClasses} data-testid="register-email" />
                  </div>
                </div>
                <div>
                  <label className="text-[#E5B1A1] text-xs uppercase tracking-[0.2em] font-medium block mb-2">{t('auth.register.phone')}</label>
                  <div className="relative">
                    <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A3A3A0]" />
                    <Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className={inputClasses} data-testid="register-phone" />
                  </div>
                </div>
                <div>
                  <label className="text-[#E5B1A1] text-xs uppercase tracking-[0.2em] font-medium block mb-2">{t('auth.register.password')}</label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A3A3A0]" />
                    <Input type={showPassword ? 'text' : 'password'} required value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="w-full pl-12 pr-12 py-3 bg-[#F5F5F5] border-[#E5E5E5] text-[#1A1A1B] focus:border-[#E5B1A1]" data-testid="register-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A3A3A0] hover:text-[#E5B1A1]">
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-[#E5B1A1] text-xs uppercase tracking-[0.2em] font-medium block mb-2">{t('auth.register.confirmPassword')}</label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A3A3A0]" />
                    <Input type={showPassword ? 'text' : 'password'} required value={formData.confirmPassword} onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })} className={inputClasses} data-testid="register-confirm-password" />
                  </div>
                </div>
                <Button type="submit" disabled={isLoading} className="w-full py-4 text-base font-medium border-0" style={{ backgroundColor: '#1A1A1B', color: '#E5B1A1' }} data-testid="register-submit">
                  {isLoading ? <div className="w-5 h-5 border-2 border-[#E5B1A1]/30 border-t-[#E5B1A1] rounded-full animate-spin mx-auto" /> : t('auth.register.submit')}
                </Button>
              </form>

              <div className="flex items-center gap-4 my-6">
                <div className="flex-1 h-px bg-[#E5E5E5]" />
                <span className="text-[#A3A3A0] text-xs">{t('auth.login.orContinueWith')}</span>
                <div className="flex-1 h-px bg-[#E5E5E5]" />
              </div>

              <Button type="button" onClick={loginWithGoogle} variant="outline" className="w-full py-3 border-[#E5E5E5] text-[#1A1A1B] hover:bg-[#F5F5F5]" data-testid="google-register">
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#E5B1A1" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#E5B1A1" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#D49B8B" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#D49B8B" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Google
              </Button>

              <p className="text-center text-[#6B6B6B] mt-6 text-sm">
                {t('auth.register.hasAccount')}{' '}
                <Link to="/login" className="text-[#E5B1A1] hover:text-[#D49B8B] font-medium" data-testid="login-link">{t('auth.register.login')}</Link>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export { LoginPage, RegisterPage };
